// src/pages/AddActivity.js

import React, { useState, useEffect } from "react";
import { useApi } from "../apiClient";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "../contexts/LanguageContext";
import locales from "../utils/locales";
import categoryOptions from "../utils/categoryOptions";
import Timer from "../components/Timer";
import ActivityNameAutocomplete from "../components/ActivityNameAutocomplete";
import { toast } from "react-hot-toast";
import { isDateWithin1Month } from "../utils/dateLimit";


// --- ฟังก์ชันช่วยสำหรับเวลาต่าง ๆ ---
function calcDuration(start, end) {
  if (!start || !end) return "";
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let diff = (eh * 60 + em) - (sh * 60 + sm);
  if (diff < 0) diff += 24 * 60; // ข้ามคืน
  if (isNaN(diff)) return "";
  if (diff >= 60 && diff % 60 !== 0)
    return `${Math.floor(diff / 60)}h ${diff % 60}min`;
  if (diff >= 60)
    return `${Math.floor(diff / 60)}h`;
  return `${diff}min`;
}
function isValidDuration(str) {
  if (!str) return false;
  str = str.trim();
  return (
    /^\d+\s*(h|hr|hrs|ชั่วโมง)?$/i.test(str) ||
    /^\d+\s*(m|min|mins|นาที)?$/i.test(str) ||
    /^\d{1,2}:\d{2}\s*-\s*\d{1,2}:\d{2}$/.test(str)
  );
}
function isZeroDuration(start, end) {
  if (!start || !end) return false;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  return sh === eh && sm === em;
}

export default function AddActivity({ userProfile }) {
  const api = useApi();
  const { lang } = useLang();
  const t = locales[lang];
  const navigate = useNavigate();
  const location = useLocation();

  // --- State ของฟอร์ม (ทั้งหมดต้องอยู่นอก if/return) ---
  const [activity, setActivity] = useState(""); // ชื่อกิจกรรม
  const [duration, setDuration] = useState(""); // ระยะเวลา
  const [startTime, setStartTime] = useState(""); // เวลาเริ่ม
  const [endTime, setEndTime] = useState(""); // เวลาสิ้นสุด
  const [mood, setMood] = useState(""); // อารมณ์
  const [note, setNote] = useState(""); // โน้ต
  const [category, setCategory] = useState("work");
  const [subCategory, setSubCategory] = useState("");
  const [activityDate, setActivityDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [timerMode, setTimerMode] = useState(false); // โหมดจับเวลา

  // --- รายการอารมณ์ (Mood) ---
  const moods = [
    { emoji: "😊", label: t.mood_happy },
    { emoji: "😌", label: t.mood_calm },
    { emoji: "😐", label: t.mood_neutral },
    { emoji: "😔", label: t.mood_sad },
    { emoji: "😠", label: t.mood_angry },
  ];

  // --- ดึง subcategory (ถ้ามี) ---
  const subcategories =
    category
      ? categoryOptions.find(c => c.value === category)?.subcategories ||
        categoryOptions.find(c => c.value === category)?.subs ||
        []
      : [];

  // --- Import timer result from MiniTimerBar ---
  useEffect(() => {
    if (location.state?.fromTimer) {
      if (location.state.activityName) setActivity(location.state.activityName);
      if (location.state.activityCategory) setCategory(location.state.activityCategory);
      if (location.state.activityDate) setActivityDate(location.state.activityDate);
      if (location.state.durationSeconds) {
        const min = Math.round(location.state.durationSeconds / 60);
        setDuration(min >= 60 ? `${Math.floor(min / 60)}h ${min % 60}min` : `${min}min`);
      }
      if (location.state.startedAt) {
        const started = new Date(location.state.startedAt);
        setStartTime(`${started.getHours().toString().padStart(2, "0")}:${started.getMinutes().toString().padStart(2, "0")}`);
      }
      if (location.state.finishedAt) {
        const finished = new Date(location.state.finishedAt);
        setEndTime(`${finished.getHours().toString().padStart(2, "0")}:${finished.getMinutes().toString().padStart(2, "0")}`);
      }
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // --- auto-calc duration ถ้าเลือกเวลา ---
  useEffect(() => {
    if (startTime && endTime) setDuration(calcDuration(startTime, endTime));
  }, [startTime, endTime]);

  const isOvernight = !!(startTime && endTime && endTime < startTime);

  // --- Submit ฟอร์ม (manual) ---
  const handleSubmit = async e => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Validate
    if (!activity || !activity.trim()) {
      setError(t.error_activity_name);
      setLoading(false);
      return;
    }
    if ((startTime && endTime) && isZeroDuration(startTime, endTime)) {
      setError(t.error_start_end_same);
      setLoading(false);
      return;
    }
    if (!startTime && !endTime && (!duration || !isValidDuration(duration))) {
      setError(t.error_duration);
      setLoading(false);
      return;
    }
    // --- LIMIT for Freemium: บันทึกย้อนหลังได้ไม่เกิน 1 เดือน ---
    if (userProfile && !userProfile.is_premium && !isDateWithin1Month(activityDate)) {
      const msg = lang === "th"
        ? "บัญชีฟรีบันทึกกิจกรรมย้อนหลังได้ไม่เกิน 1 เดือน"
        : "Freemium users can only record activities within the past month.";
      setError(msg);
      toast.error(msg);
      setLoading(false);
      return;
    }
    

    try {
      await api.activities.create({
        activity,
        duration,
        start_time: startTime || null,
        end_time: endTime || null,
        mood,
        note,
        category,
        sub_category: subCategory || null,
        activity_date: activityDate,
      });
      navigate("/");
    } catch (err) {
      setError(err.message);
    }

    setLoading(false);
  };

  // --- Cancel กลับหน้าก่อนหน้า ---
  const handleCancel = () => {
    navigate(-1);
  };

  // --- Callback หลังจับเวลาจบ ---
  const handleTimerFinish = timerData => {
    if (timerData?.startedAt && timerData?.finishedAt) {
      const pad = n => n.toString().padStart(2, "0");
      const started = new Date(timerData.startedAt);
      const ended = new Date(timerData.finishedAt);
      setStartTime(`${pad(started.getHours())}:${pad(started.getMinutes())}`);
      setEndTime(`${pad(ended.getHours())}:${pad(ended.getMinutes())}`);

      // คำนวณ duration (ถ้าเป็น duration_seconds)
      let durationMin = 0;
      if (timerData.duration_seconds !== undefined) {
        durationMin = Math.round(timerData.duration_seconds / 60);
      } else if (timerData.duration?.totalMinutes !== undefined) {
        durationMin = timerData.duration.totalMinutes;
      }
      if (durationMin >= 60) {
        setDuration(`${Math.floor(durationMin / 60)}h ${durationMin % 60}min`);
      } else {
        setDuration(`${durationMin}min`);
      }
    }
    setTimerMode(false); // กลับฟอร์มหลังจับเวลาเสร็จ
  };

  const categoryLabel = categoryOptions.find(c => c.value === category)?.label[lang] || "";

  // --- Render เฉพาะ Timer UI ถ้าอยู่ timerMode ---
  if (timerMode) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="max-w-lg mx-auto p-6 mt-0 bg-white rounded-xl shadow-lg"
      >
        <Timer
          activityName={activity}
          activityCategory={categoryLabel}
          activityDate={activityDate}
          lang={lang}
          locales={locales}
          onFinish={handleTimerFinish}
          onStop={() => setTimerMode(false)}
          onCancel={() => setTimerMode(false)}
        />
      </motion.div>
    );
  }

  // --- Main Form ---
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="max-w-lg mx-auto p-6 mt-0 bg-white rounded-xl shadow-lg"
    >
      <h2 className="text-2xl font-bold mb-3">{t.add_activity}</h2>

      {/* ชื่อกิจกรรม */}
      <ActivityNameAutocomplete
        value={activity}
        onChange={setActivity}
        placeholder={t.activity_placeholder}
      />

      {/* ปุ่ม Start Activity Timer */}
      {((activity ?? "").trim() !== "") && !timerMode && (
        <div className="mb-4">
          <button
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-400 to-pink-400 text-white font-semibold shadow hover:scale-105 transition"
            type="button"
            onClick={() => setTimerMode(true)}
          >
            ⏱️ {lang === "th" ? "เริ่มจับเวลา" : "Start Activity Timer"}
          </button>
        </div>
      )}

      {/* ฟอร์มหลัก */}
      <form className="space-y-4" onSubmit={handleSubmit}>
        {/* ช่องเวลา/Duration */}
        <div className="flex gap-2">
          <input
            type="time"
            className="border rounded-xl px-3 py-2 flex-1"
            value={startTime}
            onChange={e => setStartTime(e.target.value)}
            placeholder={t.start_time}
          />
          <span className="py-2">–</span>
          <input
            type="time"
            className="border rounded-xl px-3 py-2 flex-1"
            value={endTime}
            onChange={e => setEndTime(e.target.value)}
            placeholder={t.end_time}
          />
        </div>
        <input
          className="w-full border rounded-xl px-3 py-2"
          type="text"
          placeholder={t.duration_placeholder}
          value={duration}
          onChange={e => setDuration(e.target.value)}
          required={!startTime && !endTime}
          disabled={!!(startTime && endTime)}
        />
        {isOvernight && (
          <div className="text-xs text-amber-600 mb-1 font-medium">
            {lang === "th"
              ? "กิจกรรมนี้ข้ามคืน (ข้ามวัน)"
              : "This activity is overnight (crosses midnight)"}
          </div>
        )}

        {/* Mood Picker */}
        <div>
          <div className="mb-1 font-medium text-gray-700">{t.mood_prompt}</div>
          <div className="flex gap-2 mb-2">
            {moods.map(m => (
              <button
                key={m.emoji}
                type="button"
                aria-label={m.label}
                className={`text-2xl px-2 py-1 rounded-full border 
                  ${mood === m.emoji ? "bg-blue-100 border-blue-400" : "border-gray-300"}`}
                onClick={() => setMood(m.emoji)}
              >
                {m.emoji}
              </button>
            ))}
          </div>
        </div>
        <textarea
          className="w-full border rounded-xl px-3 py-2"
          placeholder={t.note_placeholder}
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={3}
        />
        <input
          className="w-full border rounded-xl px-3 py-2"
          type="date"
          value={activityDate}
          onChange={e => setActivityDate(e.target.value)}
          required
        />
        <select
          className="w-full border rounded-xl px-3 py-2"
          value={category}
          onChange={e => {
            setCategory(e.target.value);
            setSubCategory(""); // reset subcategory เมื่อเปลี่ยน category
          }}
        >
          {categoryOptions.map(c => (
            <option key={c.value} value={c.value}>{c.label[lang]}</option>
          ))}
        </select>
        {subcategories.length > 0 && (
          <select
            className="w-full border rounded-xl px-3 py-2"
            value={subCategory}
            onChange={e => setSubCategory(e.target.value)}
          >
            <option value="">{lang === "th" ? "สะท้อนอะไรกับใจ?" : "Pick a Life Aspect"}</option>
            {subcategories.map(sub => (
              <option key={sub.value} value={sub.value}>
                {sub.label?.[lang] || sub.label}
              </option>
            ))}
          </select>
        )}

        {/* ปุ่มบันทึก/ยกเลิก */}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 rounded-xl bg-gray-100 text-gray-700 border border-gray-200"
          >
            {t.cancel}
          </button>
          <button
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-pink-500 to-blue-400 text-white font-bold hover:scale-105 transition"
            type="submit"
            disabled={loading}
          >
            {loading ? t.loading : t.save}
          </button>
        </div>
        {error && (
          <div className="text-red-500 text-center">
            {error}
            {!userProfile?.is_premium && (
              <div className="mt-2">
                <a
                  href="/subscribe"
                  className="text-blue-500 underline text-xs"
                >
                  {lang === "th" ? "อัปเกรดเป็น Premium" : "Upgrade to Premium"}
                </a>
              </div>
            )}
          </div>
        )}
      </form>
    </motion.div>
  );
}